/* eslint-disable react-hooks/exhaustive-deps */
// AuthContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import BaseAPI from "../utils/Base.API";
import {
  AUTH_POPUP_MESSAGE_TYPE,
  isPopupCallbackRequest,
  notifyPopupAuthResultAndClose,
  redirectToGoogleLogin } from "../utils/authSession";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [googleProfile, setGoogleProfile] = useState({
    email: "",
    authEmail: "",
    isAdmin: false,
    err: 0,
    err_msg: "" });
  const [userProfile, setUserProfile] = useState({});

  const normalizeNoTrailingSlash = (url) => (url || "").replace(/\/+$/, "");

  const getFrontendBaseUrl = () => {
    const baseFromEnv = normalizeNoTrailingSlash(process.env.REACT_APP_BASEURL || "");
    if (baseFromEnv) {
      return baseFromEnv;
    }

    const origin = window.location.origin;
    const mainDir = (process.env.REACT_APP_MAINDIR || "").replace(/^\/+|\/+$/g, "");
    return mainDir ? `${origin}/${mainDir}` : origin;
  };

  const isAuthCallbackPath = (pathname) => {
    const cleanPath = normalizeNoTrailingSlash(pathname || "");
    return cleanPath.endsWith("/auth/callback") || cleanPath === "/auth/callback";
  };

  const extractEmailFromMe = (payload) => {
    if (!payload || typeof payload !== "object") {
      return "";
    }
    return payload?.email || payload?.user?.email || payload?.data?.email || "";
  };

  const extractUserFromMe = (payload) => {
    if (!payload || typeof payload !== "object") {
      return {};
    }
    return payload?.user && typeof payload.user === "object"
      ? payload.user
      : payload;
  };

  const isAdminFromUser = (user) => {
    const raw = user?.is_admin;
    if (typeof raw === "boolean") return raw;
    if (typeof raw === "number") return raw === 1;
    if (typeof raw === "string") {
      const normalized = raw.trim().toLowerCase();
      return normalized === "1" || normalized === "true" || normalized === "y";
    }
    return false;
  };

  const verifyAuthMe = async () => {
    try {
      const res = await BaseAPI.get("api/auth/me");
      const payload = res?.data?.data ?? res?.data;
      const user = extractUserFromMe(payload);
      const email = extractEmailFromMe(payload);

      if (!email) {
        throw new Error("Unable to resolve user email from /api/auth/me response");
      }

      return {
        ok: true,
        email,
        authEmail: user?.email || email,
        isAdmin: isAdminFromUser(user),
        profile: payload };
    } catch (error) {
      return {
        ok: false,
        status: error?.response?.status,
        message: error?.message || "Authentication verification failed" };
    }
  };

  const login = () => {
    const result = redirectToGoogleLogin();
    if (!result.ok) {
      const isPopupBlocked = result.mode === "popup_blocked";
      setGoogleProfile({
        email: "",
        authEmail: "",
        isAdmin: false,
        err: 1,
        err_msg: isPopupBlocked
          ? "Browser blocked Google login popup. Please allow popups for this site, then refresh and try again."
          : "Unable to start Google login popup. Please refresh and try again." });
      return;
    }
  };

  const handleCallbackFlow = async () => {
    const params = new URLSearchParams(window.location.search || "");
    const error = params.get("error");
    const isPopupCallback = isPopupCallbackRequest(window.location.search || "");

    if (error) {
      const errorMessage = `Authentication failed: ${error}`;
      if (isPopupCallback) {
        notifyPopupAuthResultAndClose({ success: false, reason: errorMessage });
        return;
      }
      setGoogleProfile({
        email: "",
        authEmail: "",
        isAdmin: false,
        err: 1,
        err_msg: errorMessage });
      return;
    }

    const verifyResult = await verifyAuthMe();
    if (!verifyResult.ok) {
      if (verifyResult.status === 401 || verifyResult.status === 404) {
        const cookieMessage =
          "Authentication cookie not found after callback. Please check backend Set-Cookie/CORS/SameSite settings.";
        if (isPopupCallback) {
          notifyPopupAuthResultAndClose({
            success: false,
            reason: cookieMessage
          });
          return;
        }
        setGoogleProfile({
          email: "",
          authEmail: "",
          isAdmin: false,
          err: 1,
          err_msg: cookieMessage });
        return;
      }

      if (isPopupCallback) {
        notifyPopupAuthResultAndClose({
          success: false,
          reason: "Authentication verification failed"
        });
        return;
      }
      setGoogleProfile({
        email: "",
        authEmail: "",
        isAdmin: false,
        err: 1,
        err_msg: "Authentication verification failed" });
      return;
    }

    setGoogleProfile({
      email: verifyResult.email,
      authEmail: verifyResult.authEmail || verifyResult.email,
      isAdmin: verifyResult.isAdmin,
      err: 0,
      err_msg: "" });

    if (isPopupCallback) {
      notifyPopupAuthResultAndClose({ success: true });
      return;
    }

    window.location.replace(`${getFrontendBaseUrl()}/`);
  };

  const checkLoginStatus = async () => {
    if (isAuthCallbackPath(window.location.pathname || "")) {
      await handleCallbackFlow();
      return;
    }

    const verifyResult = await verifyAuthMe();

    if (verifyResult.ok) {
      setGoogleProfile({
        email: verifyResult.email,
        authEmail: verifyResult.authEmail || verifyResult.email,
        isAdmin: verifyResult.isAdmin,
        err: 0,
        err_msg: "" });
      return;
    }

    if (verifyResult.status === 401 || verifyResult.status === 404) {
      login();
      return;
    }

    setGoogleProfile({
      email: "",
      authEmail: "",
      isAdmin: false,
      err: 1,
      err_msg: verifyResult.message });
  };

  const checkIsAdminPreview = () => {
    return Boolean(googleProfile?.isAdmin);
  };

  useEffect(() => {
    checkLoginStatus();
  }, []);

  useEffect(() => {
    const handlePopupMessage = (event) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      const data = event.data || {};
      if (data.type !== AUTH_POPUP_MESSAGE_TYPE) {
        return;
      }

      if (data.success) {
        checkLoginStatus();
        return;
      }

      const reason = String(data.reason || "Authentication failed");
      setGoogleProfile({
        email: "",
        authEmail: "",
        isAdmin: false,
        err: 1,
        err_msg: reason
      });
    };

    window.addEventListener("message", handlePopupMessage);
    return () => {
      window.removeEventListener("message", handlePopupMessage);
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        googleProfile,
        setGoogleProfile,
        checkLoginStatus,
        login,
        userProfile,
        setUserProfile,
        checkIsAdminPreview }}
    >
      {children}
    </AuthContext.Provider>
  );
};

